import { File as FileIcon, FileSpreadsheet, FileText, Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { UploadedAttachment } from './attachments';

/**
 * One attachment on the composer (uploading → ready/error) or — with no
 * `onRemove` — a saved chip under a sent user bubble (always `ready`).
 */
export interface AttachmentChip {
  id: string;
  name: string;
  mime: string;
  size?: number;
  status: 'uploading' | 'ready' | 'error';
  error?: string;
  uploaded?: UploadedAttachment;
  /** The raw File, only ever set on a pending composer chip (never on a saved transcript chip) — drives the image thumbnail. */
  file?: File;
}

const SPREADSHEET_MIMES = new Set(['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']);
const DOC_MIMES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
]);

function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

function fileKindIcon(mime: string) {
  if (SPREADSHEET_MIMES.has(mime)) return FileSpreadsheet;
  if (DOC_MIMES.has(mime)) return FileText;
  return FileIcon;
}

function formatSize(bytes?: number): string {
  if (bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extLabel(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i + 1).toUpperCase();
}

/** An object URL for a File, created on mount and revoked on unmount/change — the only way this ever leaks is skipping the cleanup. */
function useObjectUrl(file?: File): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

const StatusOverlay: React.FC<{ status: AttachmentChip['status'] }> = ({ status }) =>
  status === 'uploading' ? (
    <div className="absolute inset-0 flex items-center justify-center rounded-[inherit] bg-black/30" data-testid="attachment-chip-spinner">
      <Loader2 className="h-4 w-4 animate-spin text-white" />
    </div>
  ) : null;

const RemoveButton: React.FC<{ name: string; onClick: () => void }> = ({ name, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Remove ${name}`}
    className="absolute -end-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-ai-border bg-ai-surface text-ai-text opacity-0 shadow outline-none transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ai-focus-ring max-sm:opacity-100"
    data-testid="attachment-chip-remove"
  >
    <X className="h-3 w-3" />
  </button>
);

/** A pending composer chip: an image gets a ~64px rounded thumbnail, anything else a compact file card. */
const ComposerChip: React.FC<{ chip: AttachmentChip; onRemove: (id: string) => void }> = ({ chip, onRemove }) => {
  const isImage = isImageMime(chip.mime);
  const url = useObjectUrl(isImage ? chip.file : undefined);
  const remove = () => onRemove(chip.id);
  const uploading = chip.status === 'uploading';
  const errored = chip.status === 'error';

  if (isImage) {
    return (
      <div
        dir="auto"
        className={`group relative h-16 w-16 shrink-0 overflow-hidden rounded-ai-sm border ${
          errored ? 'border-ai-danger/60' : 'border-ai-border'
        }`}
        data-testid="attachment-chip"
        data-status={chip.status}
      >
        {url ? (
          <img src={url} alt={chip.name} className="h-full w-full object-cover" data-testid="attachment-thumb" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-ai-muted">
            <FileIcon className="h-5 w-5 text-ai-text-faint" />
          </div>
        )}
        <StatusOverlay status={chip.status} />
        <RemoveButton name={chip.name} onClick={remove} />
        {errored && chip.error && (
          <div className="absolute inset-x-0 bottom-0 truncate bg-ai-danger/85 px-1 text-center text-[9px] leading-tight text-white">
            {chip.error}
          </div>
        )}
      </div>
    );
  }

  const Icon = fileKindIcon(chip.mime);
  return (
    <div
      dir="auto"
      className={`group relative flex max-w-[220px] items-center gap-2 rounded-ai-sm border px-2.5 py-2 ${
        errored ? 'border-ai-danger/40 bg-ai-danger/10' : 'border-ai-border bg-ai-muted'
      } ${uploading ? 'opacity-70' : ''}`}
      data-testid="attachment-chip"
      data-status={chip.status}
    >
      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-ai-sm bg-ai-surface">
        <Icon className={`h-4 w-4 ${errored ? 'text-ai-danger' : 'text-ai-text-secondary'}`} />
        <StatusOverlay status={chip.status} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-xs font-medium ${errored ? 'text-ai-danger' : 'text-ai-text'}`}>{chip.name}</div>
        <div className="truncate text-[11px] text-ai-text-faint">
          {errored && chip.error ? chip.error : [extLabel(chip.name), formatSize(chip.size)].filter(Boolean).join(' · ')}
        </div>
      </div>
      <RemoveButton name={chip.name} onClick={remove} />
    </div>
  );
};

/** A row of file chips: pending composer chips (thumbnail / file card, always removable) when `onRemove` is given,
 *  or saved transcript chips (plain name/mime pill, no remove — we don't keep the bytes) when it is not. */
export const AttachmentChips: React.FC<{ chips: AttachmentChip[]; onRemove?: (id: string) => void }> = ({ chips, onRemove }) => {
  if (onRemove) {
    return (
      <div className="flex flex-wrap items-start gap-2" data-testid="attachment-chips">
        {chips.map((chip) => (
          <ComposerChip key={chip.id} chip={chip} onRemove={onRemove} />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="attachment-chips">
      {chips.map((chip) => (
        <div
          key={chip.id}
          dir="auto"
          className={`flex max-w-[240px] items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
            chip.status === 'error' ? 'border-ai-danger/40 bg-ai-danger/10 text-ai-danger' : 'border-ai-border bg-ai-muted text-ai-text'
          }`}
          data-testid="attachment-chip"
          data-status={chip.status}
        >
          {chip.status === 'uploading' && <Loader2 className="h-3 w-3 shrink-0 animate-spin" />}
          <span className="min-w-0 truncate">{chip.name}</span>
          {chip.status === 'error' && chip.error && <span className="shrink-0 truncate text-[11px]">{chip.error}</span>}
        </div>
      ))}
    </div>
  );
};
